import { useCallback, useMemo, useState } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useTranslate } from 'src/locales';
import { paths } from 'src/routes/paths';
import { TenantType } from 'src/types/enums';

import { useGetCompanyByIdQuery } from 'src/services/connections/connections.service';
import { ListEntityType } from 'src/services/lists/lists.types';
import { openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';

import { CompanyDetailSection } from 'src/sections/companies/detail/sections/detail-section';
import CompanyDocumentsView from 'src/sections/companies/detail/sections/company-documents';
import AddItemsModal from 'src/sections/lists/modals/add-items-modal';

import RecordPanelHeader from '../record-panel-header';
import RecordActionBar from '../record-action-bar';
import { RecordAction, RecordHeaderModel } from '../record-panel.types';

// ----------------------------------------------------------------------

enum TabValue {
  DETAIL = 'detail',
  DOCUMENTS = 'documents',
}

/**
 * Company body for the record panel's quick view (JT1-1529). Fetches the company profile and feeds the
 * shared header (logo, name, type) and action bar (Add to list, Add task). The body shows the company
 * detail card and the documents. The centred overlay (name click) renders the full company detail view.
 */
export default function CompanyRecordView({ id }: { id: string }) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const tenantType = useOrgTenant();

  const { data: company, isLoading } = useGetCompanyByIdQuery({ profile_id: id }, { skip: !id });

  const [currentTab, setCurrentTab] = useState<TabValue>(TabValue.DETAIL);

  const addToList = useBoolean();

  const handleAddTask = useCallback(() => dispatch(openTaskEditorSidesheet({ companyId: id })), [dispatch, id]);

  const actions: RecordAction[] = useMemo(() => [
    { key: 'add-to-list', label: t('common.add_to_list', { defaultValue: 'Add to list' }), icon: 'solar:list-bold', onClick: addToList.onTrue },
    { key: 'add-task', label: t('common.add_task'), icon: 'ic:round-add-task', onClick: handleAddTask },
  ], [t, addToList.onTrue, handleAddTask]);

  const header: RecordHeaderModel = useMemo(() => ({
    avatarUrl: company?.company_logo?.public_path,
    name: company?.company_name || t('common.loading', { defaultValue: 'Loading…' }),
    subtitle: company?.company_type ? t(`connections.table.company_type.${company.company_type}`) : undefined,
    meta: [],
    fullProfileHref: paths.dashboard.companies.detail(id, tenantType as TenantType, (company?.company_type ?? tenantType) as TenantType),
  }), [company, t, id, tenantType]);

  if (isLoading || !company) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1, py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <RecordPanelHeader model={header} />
      <RecordActionBar actions={actions} />

      <Tabs
        value={currentTab}
        onChange={(_e, tab) => setCurrentTab(tab)}
        sx={{ px: 2.5, mt: 1, boxShadow: (theme) => `inset 0 -2px 0 0 ${theme.palette.divider}` }}
      >
        <Tab value={TabValue.DETAIL} label={t('connections.detail.tabs.detail', { defaultValue: 'Details' })} />
        <Tab value={TabValue.DOCUMENTS} label={t('connections.detail.tabs.documents', { defaultValue: 'Documents' })} />
      </Tabs>

      {currentTab === TabValue.DETAIL && (
        <Stack sx={{ px: 3, py: 2 }}>
          <CompanyDetailSection companyProfileId={id} />
        </Stack>
      )}

      {currentTab === TabValue.DOCUMENTS && (
        <Stack sx={{ px: 3, py: 2 }}>
          <CompanyDocumentsView profileId={id} />
        </Stack>
      )}

      <AddItemsModal open={addToList.value} onClose={addToList.onFalse} onSuccess={addToList.onFalse} items={[id]} entityType={ListEntityType.COMPANY} />
    </>
  );
}
